'use client';

import React, { useRef } from 'react';
import { Control, useController } from 'react-hook-form';
import cx from 'classnames';

import styles from './styles.module.css';

interface RadioGroupProps {
  name: string;
  options: string[] | Array<{ value: string; label: string }>;
  type?: 'default' | 'pills';
  control?: Control;
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
}

const RadioGroup = ({
  name,
  options,
  type = 'default',
  control,
  defaultValue = '',
  value: propValue,
  onChange: propOnChange,
  className,
}: RadioGroupProps) => {
  // If control is provided, use react-hook-form
  const useFormControl = control !== undefined;

  let value = propValue;
  let onChange = propOnChange;

  if (useFormControl) {
    // eslint-disable-next-line
    const { field } = useController({
      name,
      control,
      defaultValue,
    });

    value = field.value;
    onChange = field.onChange;
  }

  const containerClass =
    type === 'pills' ? styles.pillsGroup : styles.radioGroup;
  const labelClass = type === 'pills' ? styles.pillLabel : styles.radioLabel;

  const suppressNextChangeRef = useRef<string | null>(null);

  return (
    <div className={cx(containerClass, className)}>
      {options.map((option, index) => {
        const isObject = typeof option === 'object' && option !== null;
        const optionValue = isObject ? option.value : option;
        const optionLabel = isObject ? option.label : option;
        const isChecked = value === optionValue;
        // Debug render
        try {
          console.log('[RadioGroup render]', {
            name,
            optionValue,
            value,
            isChecked,
          });
        } catch {}

        const handleMouseDownInput = (
          e: React.MouseEvent<HTMLInputElement>,
        ) => {
          // Intercept before the browser toggles the radio; if already selected, clear it
          if (isChecked) {
            try {
              console.log('[RadioGroup onMouseDown input] clearing', {
                name,
                optionValue,
              });
            } catch {}
            suppressNextChangeRef.current = optionValue;
            e.preventDefault();
            e.stopPropagation();
            if (onChange) onChange('');
          }
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          // Allow deselect with keyboard (Space/Enter) when focused on the selected radio
          if (isChecked && (e.key === ' ' || e.key === 'Enter')) {
            try {
              console.log('[RadioGroup onKeyDown] clearing', {
                name,
                optionValue,
                key: e.key,
              });
            } catch {}
            e.preventDefault();
            e.stopPropagation();
            if (onChange) onChange('');
          }
        };

        const handleMouseDownLabel = (
          e: React.MouseEvent<HTMLLabelElement>,
        ) => {
          if (isChecked) {
            try {
              console.log('[RadioGroup onMouseDown label] clearing', {
                name,
                optionValue,
              });
            } catch {}
            suppressNextChangeRef.current = optionValue;
            e.preventDefault();
            e.stopPropagation();
            if (onChange) onChange('');
          }
        };

        return (
          <label
            key={index}
            className={cx(labelClass)}
            onMouseDown={handleMouseDownLabel}
          >
            <input
              type="radio"
              name={name}
              value={optionValue}
              checked={isChecked}
              onMouseDown={handleMouseDownInput}
              onChange={() => {
                try {
                  console.log('[RadioGroup onChange]', {
                    name,
                    optionValue,
                    wasChecked: isChecked,
                    suppressNext: suppressNextChangeRef.current,
                  });
                } catch {}
                if (suppressNextChangeRef.current === optionValue) {
                  // ignore the synthetic change immediately following our manual clear
                  suppressNextChangeRef.current = null;
                  return;
                }
                if (!isChecked) {
                  if (onChange) onChange(optionValue);
                }
              }}
              onKeyDown={handleKeyDown}
            />
            <span>{optionLabel}</span>
          </label>
        );
      })}
    </div>
  );
};

export default RadioGroup;
